/**
 * useDwellTracking Hook
 *
 * 用途：追蹤使用者在特定頁面的停留時間
 *
 * 功能：
 * - 每秒計算使用者的有效停留時間
 * - 當使用者閒置（60秒無操作）或頁面不可見時，暫停計時
 * - 在以下情況會觸發回調並重置計時：
 *   1. 當 SearchId 更新時
 *   2. 在搜尋頁面切換找網紅或找貼文
 *   3. 離開 KolId 或 Search 頁面時
 *
 * @param callbackFn 記錄停留時間的回調函數，參數為停留時間（秒）和前一個 searchId
 */
import { usePathname, useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'
import { useIdle, useInterval } from 'react-use'
import { SearchMode } from '@/constants/search/search-mode'
import { Page } from '@/hooks/use-authorization/constants'
import getPage from '@/hooks/use-authorization/get-page'
import useSearchId from '@/hooks/use-search-id'
import SearchIdManager from '@/utils/search-id-manager'

const useDwellTracking = (
  callbackFn: (dwellTime: number, previousSearchId: string) => void,
): void => {
  const isUserIdle = useIdle(60000)
  const dwellTimeRef = useRef(0)
  const pageVisibleRef = useRef(document.visibilityState === 'visible')
  const currentSearchId = useSearchId()

  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const callbackFnRef = useRef(callbackFn)

  useEffect(() => {
    callbackFnRef.current = callbackFn
  }, [callbackFn])

  useInterval(() => {
    if (!isUserIdle && pageVisibleRef.current) {
      dwellTimeRef.current += 1
    }
  }, 1000)

  const resetTimer = (): void => {
    dwellTimeRef.current = 0
  }

  useEffect(() => {
    const handleVisibilityChange = (): void => {
      pageVisibleRef.current = document.visibilityState === 'visible'
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return (): void => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  useEffect(() => {
    const handleUpdateTask = (previousSearchId: string): void => {
      if (previousSearchId) {
        callbackFnRef.current(dwellTimeRef.current, previousSearchId)
        resetTimer()
      }
    }

    SearchIdManager.getInstance().addRegisterBeforeUpdateTask(handleUpdateTask)

    return (): void => {
      SearchIdManager.getInstance().removeRegisterBeforeUpdateTask(
        handleUpdateTask,
      )
    }
  }, [])

  useEffect(() => {
    const handleRouteChangeStart = (url: string): void => {
      const page = getPage(url)

      const currentMode = searchParams?.get('mode') ?? SearchMode.Kol
      const nextMode = new URLSearchParams(url.split('?')[1] || '').get('mode')
      const isModeChanged = currentMode !== nextMode

      // 在 search 頁面切換 mode 時
      if (currentSearchId && page === Page.Search && isModeChanged) {
        SearchIdManager.getInstance().skipNextBeforeUpdateTasks()
        callbackFnRef.current(dwellTimeRef.current, currentSearchId)
        resetTimer()
        return
      }

      if (page !== Page.KolId && page !== Page.Search && currentSearchId) {
        callbackFnRef.current(dwellTimeRef.current, currentSearchId)
        resetTimer()
      }
    }

    router.events.on('routeChangeStart', handleRouteChangeStart)

    return (): void => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
    }
  }, [router.events, router.pathname, pathname, currentSearchId, searchParams])
}

export default useDwellTracking
